import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, Pressable } from 'dripsy';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput, UniInputAmount } from '@/design-system/components/inputs';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList, CreateCardAccountStackRoutes } from './type';
import { PageHeaderModalNative } from '../../../account-management/screens/page/PageHeaderModalNative';
import { DatePicker } from '@/design-system/components/datePicker/DatePicker';
import moment from 'moment-timezone';
import { CardAccountStackRoutes } from '../../card.navigator';
import { UseInputRegularVariant } from '@/design-system/components/inputs/useInput.hook';

export interface CreateCardAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  onBackData: (data: any) => void;
}

export const CreateCardAccountNativeIndividualScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, onLayout, data, onData, func, onReturnData, refresh, onBackData } = props;
  const { t } = useTranslation('card-account');
  const { body, textMb, btnView, btn, textTitle, mobileView, mobileCodeView, mobileNumberView, inputSx, account } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();
  const navigationAny = useNavigation<any>();

  const [cardAccount, setCardAccount] = useState<any>(data);
  const [errorMsg, setErrorMsg] = useState<any>({
    dateOfBirth: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    annualIncome: undefined,
    occupation: undefined,
    position: undefined
  });

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };
    let emailReg = /^[^,]+@[\w-]+(\.[a-zA-Z]{2,}){1,}$/;
    let dateReg = /^[1-9]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;

    if ((!keysLength && !cardAccount?.individual?.dateOfBirth) || (keys.includes('dateOfBirth') && !data?.dateOfBirth)) {
      errorMsgFlag.dateOfBirth = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.dateOfBirth) || (keys.includes('dateOfBirth') && data?.dateOfBirth)) {
      if (!dateReg.test(!keysLength ? cardAccount?.individual?.dateOfBirth : data?.dateOfBirth)) {
        errorMsgFlag.dateOfBirth = t('card-account::credit-card-new-account.glossary.date-format-error');
        validateFlag = false;
      } else {
        errorMsgFlag.dateOfBirth = undefined;
      }
    }

    if ((!keysLength && !cardAccount?.individual?.firstName) || (keys.includes('firstName') && !data?.firstName)) {
      errorMsgFlag.firstName = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.firstName) || (keys.includes('firstName') && data?.firstName)) {
      errorMsgFlag.firstName = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.lastName) || (keys.includes('lastName') && !data?.lastName)) {
      errorMsgFlag.lastName = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.lastName) || (keys.includes('lastName') && data?.lastName)) {
      errorMsgFlag.lastName = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.email) || (keys.includes('email') && !data?.email)) {
      errorMsgFlag.email = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.email) || (keys.includes('email') && data?.email)) {
      if (!emailReg.test(!keysLength ? cardAccount?.individual?.email : data?.email)) {
        errorMsgFlag.email = t('card-account::credit-card-new-account.glossary.email-format-error');
        validateFlag = false;
      } else {
        errorMsgFlag.email = undefined;
      }
    }

    if ((!keysLength && !cardAccount?.individual?.annualIncome) || (keys.includes('annualIncome') && !data?.annualIncome)) {
      errorMsgFlag.annualIncome = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.annualIncome) || (keys.includes('annualIncome') && data?.annualIncome)) {
      errorMsgFlag.annualIncome = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.occupation) || (keys.includes('occupation') && !data?.occupation)) {
      errorMsgFlag.occupation = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.occupation) || (keys.includes('occupation') && data?.occupation)) {
      errorMsgFlag.occupation = undefined;
    }

    if ((!keysLength && !cardAccount?.individual?.position) || (keys.includes('position') && !data?.position)) {
      errorMsgFlag.position = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount?.individual?.position) || (keys.includes('position') && data?.position)) {
      errorMsgFlag.position = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeCreateCardAccount = async () => {
    const validate = await validateForm();
    if (validate) {
      onData({ data: cardAccount });
      navigation.navigate(CreateCardAccountStackRoutes.Document, {
        onLayout,
        data: cardAccount,
        onData,
        func,
        onReturnData,
        refresh
      });
    }
  };

  const { theme } = useDripsyTheme();

  return (
    <View sx={{ backgroundColor: '$backgroundPrimary', width: '100%' }}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={() => {
                navigation.goBack();
                onBackData(cardAccount);
              }}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account::credit-card-new-account.glossary.new-account')}
            </TextTitle>
          </View>
        </View>
      </PageHeaderModalNative>
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('card-account::credit-card-new-account.new-account-page.text--fill-out-to-create-account')}
        </TextTitle>
        <View sx={{ gap: '$5' }}>
          <UniInput
            regular={UseInputRegularVariant.date}
            value={cardAccount?.individual?.dateOfBirth}
            labelText={t('card-account::credit-card-new-account.glossary.date-of-birth')}
            errorText={errorMsg.dateOfBirth}
            onValueChange={(value) => {
              setCardAccount({ ...cardAccount, individual: { ...cardAccount?.individual, dateOfBirth: value } });
              validateForm({ dateOfBirth: value });
            }}
            placeholder={'yyyy-mm-dd'}
          />
          <View sx={{ gap: '$5', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <UniInput
              value={cardAccount?.individual?.firstName}
              labelText={t('card-account::credit-card-new-account.glossary.first-name')}
              errorText={errorMsg.firstName}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, firstName: value }
                });
                validateForm({ firstName: value });
              }}
            />
            <UniInput
              value={cardAccount?.individual?.lastName}
              labelText={t('card-account::credit-card-new-account.glossary.last-name')}
              errorText={errorMsg.lastName}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  individual: { ...cardAccount?.individual, lastName: value }
                });
                validateForm({ lastName: value });
              }}
            />
          </View>
          <UniInput
            value={cardAccount?.individual?.email}
            labelText={t('card-account::credit-card-new-account.glossary.email')}
            errorText={errorMsg.email}
            onValueChange={(value) => {
              setCardAccount({
                ...cardAccount,
                individual: { ...cardAccount?.individual, email: value }
              });
              validateForm({ email: value });
            }}
            wrapperSx={errorMsg.email ? { pb: 10 } : undefined}
          />
          <UniInput
            value={cardAccount?.individual?.address}
            labelText={t('card-account::credit-card-new-account.glossary.address')}
            onValueChange={(value) => {
              setCardAccount({
                ...cardAccount,
                individual: { ...cardAccount?.individual, address: value }
              });
            }}
          />
          <UniInputAmount
            labelText={t('card-account::credit-card-new-account.glossary.annual-income')}
            assetPrefix={false}
            maxFlag={false}
            sxWrap={{ alignItems: 'center' }}
            asset={data?.currency}
            errorText={errorMsg.annualIncome}
            value={cardAccount?.individual?.annualIncome || 0}
            onValueChange={(value) => {
              setCardAccount({
                ...cardAccount,
                individual: { ...cardAccount?.individual, annualIncome: value }
              });
              validateForm({ annualIncome: value?.toString() });
            }}
          />
          <UniInput
            value={cardAccount?.individual?.occupation}
            labelText={t('card-account::credit-card-new-account.glossary.occupation')}
            errorText={errorMsg.occupation}
            onValueChange={(value) => {
              setCardAccount({
                ...cardAccount,
                individual: { ...cardAccount?.individual, occupation: value }
              });
              validateForm({ occupation: value });
            }}
          />
          <UniInput
            value={cardAccount?.individual?.position}
            labelText={t('card-account::credit-card-new-account.glossary.position')}
            errorText={errorMsg.position}
            onValueChange={(value) => {
              setCardAccount({
                ...cardAccount,
                individual: { ...cardAccount?.individual, position: value }
              });
              validateForm({ position: value });
            }}
          />
        </View>
        <View sx={btnView}>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account::credit-card-new-account.glossary.continue')}
            onPress={changeCreateCardAccount}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$10'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textTitle: {
    marginBottom: '$1'
  },
  mobileView: {
    flexDirection: 'row'
  },
  mobileCodeView: {
    width: '30%',
    mr: '$2'
  },
  inputSx: {
    width: '100%'
  },
  mobileNumberView: {
    width: '68%'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
